<template>
  <div class="w-full flex md:gap-6 flex-col md:!px-0 gap-2 !p-6 !pb-2 md:!p-0">
    <!-- Visual CTA -->
    <div class="rounded-lg !p-0 hidden md:flex gap-3 items-center">
      <div class="h-24 flex flex-col items-center justify-center w-full image-button-wrapper">
        <ImageButton :alt="alt" size="lg" class="full-size-image-button" @click="redirectToCanva" :static="isLoading">
          <Image :src="src" rounded="sm" :alt="alt" class="full-size-image" />
        </ImageButton>
      </div>
    </div>
    <!-- TOS -->
    <p class="text-typo-secondary text-xs text-center mt-2 mb-1" v-html="tos_html"></p>
  </div>
</template>

<script setup lang="ts">
import { defineProps, inject, ref, nextTick } from "vue";
import { ImageButton, Image } from "prism";
import { I18n as I18nType } from "i18n-js";
import { CanvaOnboardingFlow, CanvaVariant, useCanvaLinkBuilder } from "@/composables/canva_link_builder";
import {
  hasMagicBrushHdResult,
  Image as ImageType,
  ProcessingState,
  ResultVariant,
} from "@/modules/internal_api/image";
import Client from "@/modules/internal_api/client";
import { rbgRedirectToCanvaClickedV101, rbgRedirectToCanvaStartedV101 } from "kaleido-event-tracker";
import { PersistentStore } from "@/stores/persistent_store";

const props = defineProps<{
  src: string;
  alt: string;
  getDataURL: Function;
  canvaVariant: CanvaVariant;
  canvaOnboardingFlow: CanvaOnboardingFlow;
  stage: any;
  stageHd: any;
  image: ImageType;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}>();

const I18n: I18nType = inject("I18n");

const tos_html = I18n.t("canva_cta.common.tos_link_html", { link: "https://www.canva.com/policies/terms-of-use/" });

const isLoading = ref(false);
const variant = ref<ResultVariant>(ResultVariant.Preview);
const preview = ref<string>(props.getDataURL(props.image, props.stage.getStage(), props.stage.getStage()));

const flavor = document.querySelector("meta[property='canva-env']").getAttribute("content");
const { buildUrl } = useCanvaLinkBuilder(flavor);

const refreshPreview = async (): Promise<void> => {
  return new Promise(async (resolve) => {
    const image = props.image;
    const isMagicBrushResult =
      props.persistentStore.previewVariant === ResultVariant.MagicBrushPreview ||
      props.persistentStore.hdVariant === ResultVariant.MagicBrushHd;
    let isHdAvailable = false;

    if (isMagicBrushResult) {
      const iterationId = props.persistentStore.magicBrushCache?.iterationId || image.meta.ai_brush_last_iteration;
      isHdAvailable = hasMagicBrushHdResult(image, iterationId);
      variant.value = isHdAvailable ? ResultVariant.MagicBrushHd : ResultVariant.MagicBrushPreview;
    } else {
      isHdAvailable = image.hdResult?.state === ProcessingState.Finished;
      variant.value = isHdAvailable ? ResultVariant.Hd : ResultVariant.Preview;
    }

    if (isHdAvailable) {
      await props.refreshAllHdLayers(image);
      preview.value = (await props.stageHd()).toDataURL({
        x: 0,
        y: 0,
        width: image.meta.hdWidth,
        height: image.meta.hdHeight,
      });
    } else {
      preview.value = props.getDataURL(image, props.stage.getStage());
    }

    resolve();
  });
};

const redirectToCanva = async () => {
  rbgRedirectToCanvaClickedV101({ image_id: props.image.meta.id, source: props.canvaOnboardingFlow });
  isLoading.value = true;

  await refreshPreview();

  const image = props.image;

  try {
    const result = await Client.uploadToBucket(image, preview.value, variant.value);

    if (result.success) {
      const url = buildUrl(result.url, props.canvaVariant, props.canvaOnboardingFlow);
      // Safari on iOS is blocking all window.open calls inside async functions
      nextTick(() => {
        rbgRedirectToCanvaStartedV101({ image_id: props.image.meta.id, source: props.canvaOnboardingFlow });
        window.open(url, "_blank");
      });
    } else {
      throw new Error("Upload to bucket failed");
    }
  } catch (error) {
    console.error("Error uploading to bucket", error);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped>
.image-button-wrapper {
  height: 100% !important;
  width: 100% !important;
  max-height: 192px !important;
  max-width: 288px !important;
}

.image-button-wrapper :deep(button) {
  width: 100% !important;
  height: 100% !important;
  max-height: 192px !important;
  max-width: 288px !important;
}

.image-button-wrapper :deep(img) {
  width: 100% !important;
  height: 100% !important;
  max-height: 192px !important;
  max-width: 288px !important;
  object-fit: cover !important;
  object-position: center !important;
}
</style>
